import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles, fade } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import EditIcon from '@material-ui/icons/Edit'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'

import {
  createChecklistItem,
  fetchChecklistSetup,
  reorderChecklistItems,
  updateChecklistItem
} from 'api'
import constants from 'app/constants'
import { confirmDialog, showToast } from 'app/helpers'

const CADENCE_LABELS = {
  'daily|morning': 'Morning Walkthrough',
  'daily|end_of_day': 'End-of-Day Closing',
  'weekly|full': 'Weekly Deep Clean',
  'monthly|full': 'Monthly Deep Clean'
}

const cadenceLabel = (t) =>
  CADENCE_LABELS[`${t.frequency}|${t.round}`] || t.name

const CAPTURE_OPTIONS = [
  { value: 'checkbox', label: 'Checkbox (one tap)' },
  { value: 'photo', label: 'Photo evidence' },
  { value: 'data', label: 'Data reading' }
]

const DATA_KIND_OPTIONS = [
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'text', label: 'Text' }
]

const EMPTY_FORM = { area: '', text: '', capture_type: 'checkbox', data_kind: '' }

const useStyles = makeStyles((theme) => {
  const accent = theme.palette.primary.main
  const line = theme.palette.divider
  const surface = theme.palette.background.default
  const isDark = theme.palette.type === 'dark'
  const surface2 = isDark ? 'rgba(255,255,255,0.06)' : '#f1efec'
  const muted = fade(theme.palette.text.primary, 0.55)
  const faint = fade(theme.palette.text.primary, 0.38)

  return {
    // A readable column beside the sidebar, not text pinned to the far left
    // and buttons to the far right of a wide screen (Satya, 23 Oct).
    root: { padding: theme.spacing(2, 3), maxWidth: 980 },
    head: {
      display: 'flex', alignItems: 'center', gap: theme.spacing(1.5),
      flexWrap: 'wrap', marginBottom: theme.spacing(2)
    },
    field: { minWidth: 220 },
    addBtn: { marginLeft: 'auto' },
    note: {
      fontSize: 12.5, color: muted, background: surface2, borderRadius: 10,
      padding: theme.spacing(1, 1.5), marginBottom: theme.spacing(2)
    },
    area: {
      fontSize: 12, fontWeight: 600, color: faint,
      margin: theme.spacing(2, 0, 0.5)
    },
    item: {
      display: 'flex', alignItems: 'center', gap: theme.spacing(1),
      background: surface, border: `1px solid ${line}`, borderRadius: 10,
      padding: theme.spacing(0.75, 1.25), marginBottom: 6
    },
    itemText: { flex: 1, fontSize: 13.5, minWidth: 0 },
    chip: {
      fontSize: 11, fontWeight: 600, color: accent, background: fade(accent, 0.1),
      borderRadius: 99, padding: '2px 8px', whiteSpace: 'nowrap', flex: '0 0 auto'
    },
    chipIcon: { fontSize: 12, verticalAlign: 'text-bottom', marginRight: 3 },
    rowBtn: { padding: 4 },
    count: { fontSize: 13, color: muted },
    empty: { textAlign: 'center', color: muted, padding: theme.spacing(6, 2) },
    loadingWrap: { textAlign: 'center', marginTop: theme.spacing(4) }
  }
})

const ChecklistSetup = () => {
  const classes = useStyles()

  const [templates, setTemplates] = useState([])
  const [templateUuid, setTemplateUuid] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // dialog state: {mode: 'add'|'edit', itemUuid?, form}
  const [editor, setEditor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchChecklistSetup()
      const rows = (res && res.templates) || []
      setTemplates(rows)
      setTemplateUuid((prev) => prev || (rows[0] ? rows[0].uuid : ''))
    } catch (error) {
      showToast('Could not load the checklists', constants.ERROR)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const template = useMemo(
    () => templates.find((t) => t.uuid === templateUuid) || null,
    [templates, templateUuid]
  )
  const items = (template && template.items) || []

  const areas = useMemo(() => {
    const seen = new Set()
    const out = []
    templates.forEach((t) => t.items.forEach((i) => {
      if (!seen.has(i.area)) { seen.add(i.area); out.push(i.area) }
    }))
    return out
  }, [templates])

  const grouped = useMemo(() => {
    const order = []
    const map = {}
    items.forEach((i) => {
      if (!map[i.area]) { map[i.area] = []; order.push(i.area) }
      map[i.area].push(i)
    })
    return order.map((area) => ({ area, rows: map[area] }))
  }, [items])

  const patchTemplateItems = useCallback((mutate) => {
    setTemplates((prev) => prev.map((t) =>
      t.uuid === templateUuid ? { ...t, items: mutate(t.items) } : t
    ))
  }, [templateUuid])

  // ---- add / edit ----
  const openAdd = () => setEditor({ mode: 'add', form: { ...EMPTY_FORM } })
  const openEdit = (item) => setEditor({
    mode: 'edit',
    itemUuid: item.uuid,
    form: {
      area: item.area,
      text: item.text,
      capture_type: item.capture_type || (item.requires_photo ? 'photo' : 'checkbox'),
      data_kind: item.data_kind || ''
    }
  })
  const closeEditor = () => setEditor(null)
  const setForm = (patch) =>
    setEditor((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : prev))

  const formValid = editor && editor.form.area.trim() && editor.form.text.trim() &&
    (editor.form.capture_type !== 'data' || editor.form.data_kind)

  const saveEditor = async () => {
    if (!editor || !template) return
    const payload = {
      area: editor.form.area.trim(),
      text: editor.form.text.trim(),
      capture_type: editor.form.capture_type,
      data_kind: editor.form.capture_type === 'data' ? editor.form.data_kind : ''
    }
    setSaving(true)
    try {
      if (editor.mode === 'add') {
        const created = await createChecklistItem({ ...payload, template: template.uuid })
        patchTemplateItems((rows) => [...rows, created])
        showToast('Checkpoint added — applies from the next run', constants.SUCCESS)
      } else {
        const replacement = await updateChecklistItem(editor.itemUuid, payload)
        patchTemplateItems((rows) =>
          rows.map((r) => (r.uuid === editor.itemUuid ? replacement : r))
        )
        showToast('Checkpoint updated — applies from the next run', constants.SUCCESS)
      }
      closeEditor()
    } catch (error) {
      const data = error && error.response && error.response.data
      showToast((data && data.detail) || 'Could not save the checkpoint', constants.ERROR)
    } finally {
      setSaving(false)
    }
  }

  // ---- retire ----
  const retire = async (item) => {
    const { isConfirmed } = await confirmDialog({
      text:
        `Remove "${item.text}" from the ${template ? cadenceLabel(template).toLowerCase() : 'checklist'}? ` +
        'Past submissions keep it; centres stop seeing it from the next run.',
      confirmButtonText: 'Yes, remove it'
    })
    if (!isConfirmed) return
    try {
      await updateChecklistItem(item.uuid, { active: false })
      patchTemplateItems((rows) => rows.filter((r) => r.uuid !== item.uuid))
      showToast('Checkpoint removed', constants.SUCCESS)
    } catch (error) {
      showToast('Could not remove the checkpoint', constants.ERROR)
    }
  }

  // ---- reorder (swap within the template's flat order) ----
  const move = async (item, delta) => {
    const idx = items.findIndex((r) => r.uuid === item.uuid)
    const target = idx + delta
    if (idx < 0 || target < 0 || target >= items.length) return
    const next = [...items]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    patchTemplateItems(() => next)
    try {
      await reorderChecklistItems(template.uuid, next.map((r) => r.uuid))
    } catch (error) {
      patchTemplateItems(() => items)
      showToast('Could not reorder', constants.ERROR)
    }
  }

  const captureChip = (item) => {
    if (item.capture_type === 'photo' || item.requires_photo) {
      return <span className={classes.chip}><PhotoCameraIcon className={classes.chipIcon} />photo</span>
    }
    if (item.capture_type === 'data') {
      return <span className={classes.chip}>{item.data_kind || 'data'}</span>
    }
    return null
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.head}>
        <FormControl size="small">
          <Select
            className={classes.field}
            value={templateUuid}
            onChange={(e) => setTemplateUuid(e.target.value)}
            displayEmpty
          >
            {templates.map((t) => (
              <MenuItem key={t.uuid} value={t.uuid}>{cadenceLabel(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {template && (
          <Typography className={classes.count}>{items.length} checkpoints</Typography>
        )}
        <Button
          className={classes.addBtn}
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={openAdd}
          disabled={!template}
        >
          Add checkpoint
        </Button>
      </Box>

      <Typography className={classes.note}>
        Edits apply from the next run of the checklist — today’s open checklists keep the
        checkpoints they started with, and past submissions are never rewritten.
      </Typography>

      {loading && <Box className={classes.loadingWrap}><CircularProgress /></Box>}
      {!loading && !template && (
        <Typography className={classes.empty}>No active checklists found.</Typography>
      )}
      {!loading && template && grouped.map(({ area, rows }) => (
        <Box key={area}>
          <Typography className={classes.area}>{area}</Typography>
          {rows.map((item) => (
            <Box key={item.uuid} className={classes.item}>
              <Typography className={classes.itemText}>{item.text}</Typography>
              {captureChip(item)}
              <IconButton className={classes.rowBtn} size="small" onClick={() => move(item, -1)}>
                <ArrowUpwardIcon fontSize="inherit" />
              </IconButton>
              <IconButton className={classes.rowBtn} size="small" onClick={() => move(item, 1)}>
                <ArrowDownwardIcon fontSize="inherit" />
              </IconButton>
              <IconButton className={classes.rowBtn} size="small" onClick={() => openEdit(item)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
              <IconButton className={classes.rowBtn} size="small" onClick={() => retire(item)}>
                <DeleteOutlineIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ))}

      <Dialog open={Boolean(editor)} onClose={closeEditor} fullWidth maxWidth="xs">
        <DialogTitle>
          {editor && editor.mode === 'add' ? 'Add checkpoint' : 'Edit checkpoint'}
        </DialogTitle>
        <DialogContent dividers>
          {editor && (
            <>
              <TextField
                label="Area"
                value={editor.form.area}
                onChange={(e) => setForm({ area: e.target.value })}
                fullWidth
                margin="dense"
                inputProps={{ list: 'checklist-areas' }}
                helperText="Pick an existing area or type a new one"
              />
              <datalist id="checklist-areas">
                {areas.map((a) => <option key={a} value={a} />)}
              </datalist>
              <TextField
                label="Checkpoint"
                value={editor.form.text}
                onChange={(e) => setForm({ text: e.target.value })}
                fullWidth
                margin="dense"
                multiline
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>How it’s completed</InputLabel>
                <Select
                  value={editor.form.capture_type}
                  onChange={(e) => setForm({
                    capture_type: e.target.value,
                    data_kind: e.target.value === 'data' ? (editor.form.data_kind || 'number') : ''
                  })}
                >
                  {CAPTURE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {editor.form.capture_type === 'data' && (
                <FormControl fullWidth margin="dense">
                  <InputLabel>Reading type</InputLabel>
                  <Select
                    value={editor.form.data_kind}
                    onChange={(e) => setForm({ data_kind: e.target.value })}
                  >
                    {DATA_KIND_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor}>Cancel</Button>
          <Button
            onClick={saveEditor}
            color="primary"
            variant="contained"
            disabled={!formValid || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ChecklistSetup
